import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Upload } from 'lucide-react';
import { cn } from '../../lib/cn';
import { formatFileSize } from '../../lib/format';
import { FieldError } from './Field';
import { buttonClasses } from './buttonStyles';

export interface FileDropProps {
  label: ReactNode;
  hint?: ReactNode;
  /** e.g. ".pdf,.jpg,.jpeg,.png,.heic,.docx" or "image/*" (FR-2.1 list). */
  accept?: string;
  /** Default 25 MB (FR-2.1). */
  maxSizeBytes?: number;
  multiple?: boolean;
  /** Ask phones to open the camera ("environment" = back camera). */
  capture?: 'user' | 'environment';
  disabled?: boolean;
  /** Error from the form/server, shown with the control. */
  error?: string | null;
  onFiles: (files: File[]) => void;
  className?: string;
}

function matchesAccept(file: File, accept: string | undefined): boolean {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  return accept
    .split(',')
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean)
    .some((a) => {
      if (a.startsWith('.')) return name.endsWith(a);
      if (a.endsWith('/*')) return file.type.toLowerCase().startsWith(a.slice(0, -1));
      return file.type.toLowerCase() === a;
    });
}

/**
 * Drag-and-drop area plus a real "Choose file" button (keyboard and screen-reader friendly).
 * Validates type and size before calling `onFiles`.
 */
export function FileDrop({
  label,
  hint,
  accept,
  maxSizeBytes = 25 * 1024 * 1024,
  multiple,
  capture,
  disabled,
  error,
  onFiles,
  className,
}: FileDropProps) {
  const id = `fd${useId().replace(/:/g, '')}`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const shownError = error ?? localError;

  const handle = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const files = Array.from(list).slice(0, multiple ? undefined : 1);
    for (const f of files) {
      if (!matchesAccept(f, accept)) {
        setLocalError(`"${f.name}" is not a file type we can store. Use one of: ${accept}.`);
        return;
      }
      if (f.size > maxSizeBytes) {
        setLocalError(`"${f.name}" is ${formatFileSize(f.size)}. The largest file allowed is ${formatFileSize(maxSizeBytes)}.`);
        return;
      }
    }
    setLocalError(null);
    onFiles(files);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) handle(e.dataTransfer.files);
  };

  return (
    <div className={cn('flex flex-col gap-2', shownError && 'border-l-4 border-danger pl-3', className)}>
      <label htmlFor={id} className="font-bold">
        {label}
      </label>
      {hint && (
        <p id={`${id}-hint`} className="text-muted">
          {hint}
        </p>
      )}
      {shownError && <FieldError id={`${id}-error`}>{shownError}</FieldError>}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center gap-3 rounded-[var(--radius-card)] border-2 border-dashed p-6 text-center',
          dragging ? 'border-primary bg-primary-soft' : 'border-border-strong bg-surface',
          disabled && 'opacity-60',
        )}
      >
        <Upload aria-hidden="true" className="h-8 w-8 text-primary" />
        <p className="text-muted">Drag a file here, or</p>
        <input
          ref={inputRef}
          id={id}
          type="file"
          className="peer sr-only"
          accept={accept}
          multiple={multiple}
          capture={capture}
          disabled={disabled}
          aria-describedby={cn(shownError && `${id}-error`, hint && `${id}-hint`) || undefined}
          aria-invalid={shownError ? true : undefined}
          onChange={(e) => {
            handle(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          className={buttonClasses({
            variant: 'secondary',
            className: 'peer-focus-visible:outline-[3px] peer-focus-visible:outline-offset-2 peer-focus-visible:outline-focus peer-focus-visible:outline',
          })}
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          aria-hidden="true"
          tabIndex={-1}
        >
          <span>{multiple ? 'Choose files' : 'Choose a file'}</span>
        </button>
        <p className="text-sm text-muted">Up to {formatFileSize(maxSizeBytes)}</p>
      </div>
    </div>
  );
}
